/**
 * Body Atlas (Disciplines DS4.3) — data + tiers for the realistic muscle map.
 *
 * Imported from a Claude Design handoff ("Body Atlas"). The figure visuals are
 * anatomical PNGs (public/bodyatlas/bg) + per-muscle region geometry
 * (public/bodyatlas/data/{gender}-{view}.json — `{vb, ar, p:[[pathD,key,base]]}`).
 * Coloring is driven by each muscle GROUP's tier (group level → 1 of 10 rarity
 * tiers); the 18 individual muscles inherit their group's tier for now.
 */

/**
 * Which figure to DRAW. ⚠️⚠️ This is a STORAGE KEY, not just a label — it is
 * interpolated straight into asset paths (`/bodyatlas/data/{gender}-front.json`
 * and `/bodyatlas/bg/{gender}-{view}.…`), so it can only ever be one of these
 * two values. A third member would 404 the figure.
 *
 * ⭐ The user's ANSWER is a different thing and is nullable — see
 * `bodyAtlasStore.gender` and `DEFAULT_FIGURE` below.
 */
export type BodyGender = 'male' | 'female';

/**
 * What to render before the user has answered.
 *
 * ⚠️⚠️ A RENDERING FALLBACK, NEVER A STORED VALUE. Until 2026-08-26 the store
 * collapsed "no answer" into `'male'` and persisted it, so every user who never
 * opened Settings → Personal Details silently became male — the figure was a
 * guess presented as a fact, and any feature gating on `=== 'female'` would
 * have been invisible to the people it was for while appearing to work.
 * Draw something, but never write it down.
 */
export const DEFAULT_FIGURE: BodyGender = 'male';

export type BodyView = 'front' | 'back';
/** Figure colourway: the cyan X-ray ('blue') or the desaturated anatomy ('mono'). */
export type BodyColor = 'blue' | 'mono';

/**
 * The 7 muscle groups — identical to our fitnessStore muscle groups. The lower
 * body is split into "Upper Legs" (quads + hamstrings + glutes) and
 * "Lower Legs" (calves); the region JSON traces these separately.
 */
export const BODY_GROUPS = ['chest', 'shoulders', 'arms', 'core', 'back', 'upper-legs', 'lower-legs'] as const;
export type BodyGroupId = (typeof BODY_GROUPS)[number];

export const BODY_GROUP_NAMES: Record<BodyGroupId, string> = {
  chest: 'Chest',
  shoulders: 'Shoulders',
  arms: 'Arms',
  core: 'Core',
  back: 'Back',
  'upper-legs': 'Upper Legs',
  'lower-legs': 'Lower Legs',
};

/** group id → the matching `fitness_parameters` muscle-group name (parameterName). */
export const GROUP_ID_TO_PARAM_NAME: Record<BodyGroupId, string> = {
  chest: 'Chest',
  shoulders: 'Shoulders',
  arms: 'Arms',
  core: 'Core',
  back: 'Back',
  'upper-legs': 'Upper Legs',
  'lower-legs': 'Lower Legs',
};

export interface BodyMuscle {
  key: string;
  name: string;
  group: BodyGroupId;
  views: BodyView[];
}

/** The 18 individual muscles (region keys in the JSON map to these). */
export const BODY_MUSCLES: BodyMuscle[] = [
  { key: 'upper-chest', name: 'Upper chest', group: 'chest', views: ['front'] },
  { key: 'lower-chest', name: 'Lower chest', group: 'chest', views: ['front'] },
  { key: 'front-delts', name: 'Front delts', group: 'shoulders', views: ['front'] },
  { key: 'rear-delts', name: 'Rear delts', group: 'shoulders', views: ['back'] },
  { key: 'neck', name: 'Neck', group: 'shoulders', views: ['front', 'back'] },
  { key: 'biceps', name: 'Biceps', group: 'arms', views: ['front'] },
  { key: 'triceps', name: 'Triceps', group: 'arms', views: ['back'] },
  { key: 'forearms', name: 'Forearms', group: 'arms', views: ['front', 'back'] },
  { key: 'upper-abs', name: 'Upper abs', group: 'core', views: ['front'] },
  { key: 'lower-abs', name: 'Lower abs', group: 'core', views: ['front'] },
  { key: 'obliques', name: 'Obliques', group: 'core', views: ['front'] },
  { key: 'traps', name: 'Traps', group: 'back', views: ['back'] },
  { key: 'lats', name: 'Lats', group: 'back', views: ['back'] },
  { key: 'lower-back', name: 'Lower back', group: 'back', views: ['back'] },
  { key: 'glutes', name: 'Glutes', group: 'upper-legs', views: ['back'] },
  { key: 'quads', name: 'Quads', group: 'upper-legs', views: ['front'] },
  { key: 'hamstrings', name: 'Hamstrings', group: 'upper-legs', views: ['back'] },
  { key: 'calves', name: 'Calves', group: 'lower-legs', views: ['front', 'back'] },
];

export const MUSCLE_BY_KEY: Record<string, BodyMuscle> = Object.fromEntries(
  BODY_MUSCLES.map(m => [m.key, m])
);

export function keysOfGroup(group: BodyGroupId): string[] {
  return BODY_MUSCLES.filter(m => m.group === group).map(m => m.key);
}

/** The 10 rarity tiers (name + color), from the Body Atlas design. */
export interface RarityTier {
  index: number;
  name: string;
  color: string;
}

export const RARITY_TIERS: RarityTier[] = [
  { index: 0, name: 'Common', color: '#9AA0A6' },
  { index: 1, name: 'Frequent', color: '#E6E9ED' },
  { index: 2, name: 'Uncommon', color: '#2FC2E8' },
  { index: 3, name: 'Scarce', color: '#2C74E0' },
  { index: 4, name: 'Rare', color: '#3FB950' },
  { index: 5, name: 'Epic', color: '#F2D118' },
  { index: 6, name: 'Legendary', color: '#F2902A' },
  { index: 7, name: 'Mythic', color: '#ED2F2F' },
  { index: 8, name: 'Chimeric', color: '#F33D9F' },
  { index: 9, name: 'Artefact', color: '#B24FE6' },
];

/** Minimum group level for each tier (index-aligned with RARITY_TIERS). Tunable. */
export const TIER_MIN_LEVEL = [1, 3, 5, 8, 12, 17, 24, 33, 45, 61];

/**
 * Map a muscle group's level + XP to a rarity tier. Untrained (no XP) → null
 * (the figure shows the bare photo until a group earns XP, then it lights up).
 */
export function groupTier(level: number, xp: number): RarityTier | null {
  if (!xp || xp <= 0) return null;
  let idx = 0;
  for (let i = 0; i < TIER_MIN_LEVEL.length; i++) {
    if (level >= TIER_MIN_LEVEL[i]) idx = i;
  }
  return RARITY_TIERS[idx];
}
